import React, { useEffect, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import {
  Grid,
  Paper,
  Typography,
  MenuItem,
  Select,
  Fab,
  FormControl,
  InputLabel,
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  Divider,
  Tooltip,
} from "@mui/material";
import { translate } from "../../../translate/translate";
import { Search, Close, Visibility as VisibilityIcon, MoreHoriz as MoreHorizIcon, Download } from "@mui/icons-material";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import { toast } from "react-toastify";
import TablePaginationComponent from "../../../components/table/table-pagination.component";
import { BaseService } from "../../../services/base-service";
import { Stock } from "../../../models/general/stock";
import { PaginatedResult } from "../../../models/default/paginated-result";
import { ErrorComponent } from "../../../components/error/error.component";
import { URLS } from "../../../services/app-urls";
import { parseLocaleNumber } from "../../../utils/locale-number-utils";
import { ExportType } from "../../../types/export.types";
import { useAuth } from "../../../contexts/auth-context";
import { useExportProgress } from "../../../hooks/useExportProgress";
import { buildStockFileName, downloadExportFile } from "../../../utils/download-utils";
import Loading from "../../../components/loading/loading";

const MATERIAL_TYPE_OPTIONS = ["ZROH", "ZHLB", "ZMOD"];
const LEFT_ALIGNED_HEADER_COLUMNS = new Set([
  "part_number",
  "description",
  "bom",
  "supplier",
  "manufacturer",
  "modified_at",
]);
const LEFT_ALIGNED_BODY_COLUMNS = new Set([
  "part_number",
  "description",
  "boms",
  "suppliers.name",
  "suppliers.supplier_part_number",
  "stock_modified_at",
]);
const ON_HAND_COLUMN_PADDING_RIGHT = "56px";

const StockComponent = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const service = new BaseService<Stock>(URLS.COMPONENT);
  const serviceExport = new BaseService<any>(URLS.STOCK_EXPORT);

  const { user } = useAuth();
  const { start: startExport } = useExportProgress(ExportType.EXPORT_STOCK);
  const [dataSource, setDataSource] = useState<Stock[]>([]);
  const [pagination, setPagination] = useState<PaginatedResult<any>>();
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [filterMaterialType, setFilterMaterialType] = useState("all");
  const [filterObsolete, setFilterObsolete] = useState<string>(searchParams.get("filterObsolete") || (location.state as any)?.filterObsolete || "all");
  const [filterInTest, setFilterInTest] = useState<string>("all");

  const [filterPartNumberStr, setFilterPartNumberStr] = useState<string>("");

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState<string[]>([]);
  const [showCopyMessage, setShowCopyMessage] = useState(false);
  const [modalTitle, setModalTitle] = useState<string>("");

  const handleOpenModal = (title: string, items: string[]) => {
    setModalTitle(title);
    setModalContent(items);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setModalContent([]);
    setShowCopyMessage(false);
  };

  const handleCopyItems = async () => {
    try {
      const itemsString = modalContent.join(", ");
      if (itemsString) {
        await navigator.clipboard.writeText(itemsString);
      }
      setShowCopyMessage(true);
    } catch (err) {
      toast.error(translate.t("copy_failed"));
    }
    setTimeout(() => {
      setShowCopyMessage(false);
    }, 2000);
  };

  const renderExpandableList = (title: string, items: string[]) => {
    if (!items || items.length === 0) return "-";
    const limited = items.slice(0, 1);
    const isClickable = items.length > 1;

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          position: "relative",
          "&:hover .more-icon": { opacity: 0 },
          "&:hover .eye-icon": { opacity: 1 },
        }}
      >
        {limited.map((item: string, idx: number) => (
          <span
            key={idx}
            style={{ cursor: isClickable ? "pointer" : "default", marginBottom: "4px" }}
            onClick={() => isClickable && handleOpenModal(title, items)}
          >
            {item}
          </span>
        ))}

        {isClickable && (
          <Box
            onClick={() => handleOpenModal(title, items)}
            sx={{
              fontWeight: 600,
              cursor: "pointer",
              marginTop: 1,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              position: "relative",
              width: "fit-content",
            }}
          >
            <MoreHorizIcon className="more-icon" sx={{ transition: "opacity 0.2s", color: "#90A4AE" }} />
            <VisibilityIcon
              className="eye-icon"
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                opacity: 0,
                transition: "opacity 0.2s",
                color: "#90A4AE",
              }}
            />
          </Box>
        )}
      </Box>
    );
  };

  const renderCompactList = (title: string, items: string[]) => {
    if (!items || items.length === 0) return "-";

    const uniqueItems = Array.from(new Set(items));
    const visibleItems = uniqueItems.slice(0, 1);
    const remainingCount = uniqueItems.length - visibleItems.length;
    const fullText = uniqueItems.join(", ");

    return (
      <Tooltip title={fullText}>
        <Box
          component="span"
          onClick={() => uniqueItems.length > 1 && handleOpenModal(title, uniqueItems)}
          sx={{
            display: "block",
            maxWidth: "100%",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            cursor: uniqueItems.length > 1 ? "pointer" : "default",
            "&:hover .more-icon": { opacity: 0 },
            "&:hover .eye-icon": { opacity: 1 },
          }}
        >
          {visibleItems.join(", ")}
          {remainingCount > 0 && (
            <Box component="span" sx={{ color: "#607883", marginLeft: "4px" }}>
              {`(+${remainingCount})`}
            </Box>
          )}
          {remainingCount > 0 && (
            <Box
              component="span"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                marginLeft: "4px",
                position: "relative",
                width: "20px",
                height: "20px",
                verticalAlign: "middle",
              }}
            >
              <MoreHorizIcon className="more-icon" sx={{ transition: "opacity 0.2s", color: "#90A4AE", fontSize: "20px" }} />
              <VisibilityIcon
                className="eye-icon"
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  opacity: 0,
                  transition: "opacity 0.2s",
                  color: "#90A4AE",
                  fontSize: "18px",
                }}
              />
            </Box>
          )}
        </Box>
      </Tooltip>
    );
  };

  const search = () => {
    setIsLoading(true);
    service.clearParameters();

    const searchPartNumbers = filterPartNumberStr
      .split(";")
      .map((item) => item.trim())
      .filter((item) => item !== "");

    if (searchPartNumbers.length > 0) service.addParameter("part_number__in", searchPartNumbers.join(","));
    if (filterMaterialType !== "all")
      service.addParameter("matl_type", filterMaterialType);
    if (filterObsolete !== "all")
      service.addParameter("is_obsolete", filterObsolete === "true");
    if (filterInTest !== "all")
      service.addParameter("is_in_test", filterInTest === "true");

    service.addParameter("limit", rowsPerPage);
    service.addParameter("offset", page * rowsPerPage);

    service
      .getAllPaginate()
      .then((response) => {
        setPagination(response.data);
        setDataSource(response.data.results);
      })
      .catch((error: any) => {
        ErrorComponent(error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    search();
  }, [page, rowsPerPage, filterMaterialType, filterObsolete, filterInTest]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const renderCellHandler = (column: string, _index: number, row: any) => {
    if (column === "quantity") {
      return parseLocaleNumber(Number(row[column]));
    }
    if (column === "stock_modified_at") {
      if (!row[column]) return "-";
      const date = new Date(row[column]);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${day}/${month}/${year} ${hours}:${minutes}`;
    }
    if (column === "suppliers.name") {
      let items = row.suppliers && row.suppliers.length > 0
        ? row.suppliers.map((s: any) => s.name).filter(Boolean)
        : [];
      items = Array.from(new Set(items));
      return items.length > 0 ? renderExpandableList(translate.t("supplier"), items as string[]) : "";
    }
    if (column === "boms") {
      let items = row.boms && row.boms.length > 0
        ? row.boms.map((b: any) => typeof b === 'string' ? b : (b.bom_number || b.name || b.id)).filter(Boolean)
        : [];
      items = Array.from(new Set(items));
      return items.length > 0 ? renderCompactList(translate.t("bom"), items as string[]) : "";
    }
    if (column === "suppliers.supplier_part_number") {
      let items = row.suppliers && row.suppliers.length > 0
        ? row.suppliers.map((s: any) => s.supplier_part_number).filter(Boolean)
        : [];
      items = Array.from(new Set(items));
      return items.length > 0 ? renderExpandableList(translate.t("manufacturers"), items as string[]) : "";
    }
    if (column === "description") {
      const val = row[column];
      if (!val) return "";
      const description = Array.isArray(val) ? val.join(", ") : String(val);
      return (
        <Tooltip title={description}>
          <Box
            component="span"
            sx={{
              display: "block",
              maxWidth: "100%",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {description}
          </Box>
        </Tooltip>
      );
    }
    return row[column];
  };

  const handleExportStock = async () => {
    try {
      serviceExport.clearParameters();

      const searchPartNumbers = filterPartNumberStr
        .split(";")
        .map((item) => item.trim())
        .filter((item) => item !== "");

      const searchQuery = searchPartNumbers.join(",");
      const hasFilters = !!(searchQuery || filterMaterialType !== "all" || filterObsolete !== "all" || filterInTest !== "all");

      if (searchQuery) serviceExport.addParameter("part_number__in", searchQuery);
      if (filterMaterialType !== "all") serviceExport.addParameter("matl_type", filterMaterialType);
      if (filterObsolete !== "all") serviceExport.addParameter("is_obsolete", filterObsolete === "true");
      if (filterInTest !== "all") serviceExport.addParameter("is_in_test", filterInTest === "true");

      const startResponse = await serviceExport.loadUrlTyped<{
        task_id?: string;
        file_url?: string;
        filename?: string;
        status?: string;
      }>(URLS.STOCK_EXPORT);

      const { task_id, file_url, filename, status } = startResponse.data;
      const fileName = buildStockFileName(hasFilters);

      const exportFileReference = file_url || filename;

      if (!task_id && status === "FINISHED" && exportFileReference) {
        toast.success(translate.t("export_already_created_downloading"));
        try {
          await downloadExportFile(exportFileReference, { fileName }, "stock");
        } catch {
          toast.error(translate.t("error_downloading_file_try_again"));
        }
        return;
      }

      if (task_id && user?.id) {
        startExport(hasFilters);
        toast.info(translate.t("export_started_notification"));
        return;
      }

      toast.error(translate.t("invalid_api_response"));
    } catch (error) {
      console.error(translate.t("export_error"), error);
      toast.error(translate.t("invalid_api_response"));
    }
  };


  return (
    <section>
      <Loading open={isLoading} />
      <div className="floating-title" style={{ display: "flex", alignItems: "center" }}>
        <Typography variant="h5">{translate.t("stock")}</Typography>
      </div>

      <Paper variant="outlined" sx={{ padding: "16px", marginBottom: "16px" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "nowrap" }}>
          <Box sx={{ flex: 1, minWidth: 180 }}>
            <TextField
              fullWidth
              variant="outlined"
              label={translate.t("part_number")}
              placeholder={translate.t("part_number")}
              value={filterPartNumberStr}
              onChange={(e) => setFilterPartNumberStr(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  if (page === 0) {
                    search();
                  } else {
                    setPage(0);
                  }
                }
              }}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Box>
          <Box sx={{ flex: "0 0 170px" }}>
            <FormControl fullWidth variant="outlined">
              <InputLabel id="filter-material-type-label">
                {translate.t("material_type")}
              </InputLabel>
              <Select
                labelId="filter-material-type-label"
                value={filterMaterialType}
                onChange={(e) => {
                  setFilterMaterialType(e.target.value);
                  setPage(0);
                }}
                label={translate.t("material_type")}
              >
                <MenuItem value="all">{translate.t("all")}</MenuItem>
                {MATERIAL_TYPE_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ flex: "0 0 160px" }}>
            <FormControl fullWidth variant="outlined">
              <InputLabel id="filter-obsolete-label">
                {translate.t("partNumber_status")}
              </InputLabel>
              <Select
                labelId="filter-obsolete-label"
                value={filterObsolete}
                onChange={(e) => {
                  setFilterObsolete(e.target.value);
                  setPage(0);
                }}
                label={translate.t("partNumber_status")}
              >
                <MenuItem value="all">{translate.t("all_items")}</MenuItem>
                <MenuItem value="true">{translate.t("obsolete_items")}</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ flex: "0 0 160px" }}>
            <FormControl fullWidth variant="outlined">
              <InputLabel id="filter-in-test-label">
                {translate.t("is_in_test")}
              </InputLabel>
              <Select
                labelId="filter-in-test-label"
                value={filterInTest}
                onChange={(e) => {
                  setFilterInTest(e.target.value);
                  setPage(0);
                }}
                label={translate.t("is_in_test")}
              >
                <MenuItem value="all">{translate.t("all")}</MenuItem>
                <MenuItem value="true">{translate.t("yes")}</MenuItem>
                <MenuItem value="false">{translate.t("no")}</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Fab
            size="small"
            type="submit"
            color="primary"
            onClick={() => {
              if (page === 0) {
                search();
              } else {
                setPage(0);
              }
            }}
            sx={{
              flexShrink: 0,
              backgroundColor: "#3949AB",
              color: "white",
              "&:hover": { backgroundColor: "#283593" }
            }}
          >
            <Search />
          </Fab>
          <Divider
            orientation="vertical"
            flexItem
            sx={{ height: "40px", alignSelf: "center" }}
          />
          <Button
            startIcon={<Download />}
            variant="outlined"
            color="primary"
            sx={{ flexShrink: 0, whiteSpace: "nowrap" }}
            onClick={handleExportStock}
          >
            {translate.t("export_analysis")}
          </Button>
        </Box>
      </Paper>

      <Paper variant="outlined">
        <TablePaginationComponent
          state={dataSource}
          displayedColumnsShow={[
            "part_number",
            "material_type",
            "description",
            "bom",
            "on_hand",
            "supplier",
            "manufacturer",
            "is_obsolete",
            "modified_at"
          ]}
          displayedColumns={[
            "part_number",
            "matl_type",
            "description",
            "boms",
            "quantity",
            "suppliers.name",
            "suppliers.supplier_part_number",
            "is_obsolete",
            "stock_modified_at"
          ]}
          readOnlyBooleanFieldList={["is_obsolete"]}
          columnStyles={{
            part_number: "10%",
            material_type: "10%",
            matl_type: "10%",
            description: "25%",
            bom: "12%",
            on_hand: "8%",
            supplier: "10%",
            manufacturer: "10%",
            is_obsolete: "6%",
            modified_at: "9%",
          }}
          count={pagination?.count}
          next={pagination?.next}
          previous={pagination?.previous}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          renderCellHandler={{
            "quantity": renderCellHandler,
            "stock_modified_at": renderCellHandler,
            "suppliers.name": renderCellHandler,
            "boms": renderCellHandler,
            "suppliers.supplier_part_number": renderCellHandler,
            "description": renderCellHandler
          }}
          headerCellCustomStyleHandler={(column: string) => ({
            textAlign: column === "on_hand" ? "right" : LEFT_ALIGNED_HEADER_COLUMNS.has(column) ? "left" : "center",
            ...(column === "on_hand"
              ? {
                paddingRight: ON_HAND_COLUMN_PADDING_RIGHT,
              }
              : {}),
          })}
          cellCustomStyleHandler={(column: string) => ({
            textAlign: column === "quantity" ? "right" : LEFT_ALIGNED_BODY_COLUMNS.has(column) ? "left" : "center",
            ...(column === "quantity"
              ? {
                paddingRight: ON_HAND_COLUMN_PADDING_RIGHT,
                fontVariantNumeric: "tabular-nums",
              }
              : {}),
            ...(column === "description" || column === "boms"
              ? {
                overflow: "hidden",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
              }
              : {}),
          })}
        />
      </Paper>

      <Dialog open={isModalOpen} onClose={handleCloseModal}>
        <Grid
          padding={2}
          container
          justifyContent="space-between"
          sx={{ backgroundColor: "primary.main" }}
        >
          <Grid item>
            <Typography
              sx={{ color: "#ffffff", fontWeight: "bold", fontSize: "16px" }}
            >
              {modalTitle}
            </Typography>
          </Grid>
          <Grid item sx={{ cursor: "pointer", color: "#ffffff" }}>
            <Close onClick={handleCloseModal} />
          </Grid>
        </Grid>
        <DialogContent sx={{ minWidth: "500px" }}>
          <Grid container flexDirection="column" gap={1}>
            <Grid item>
              <Typography>
                <b>{translate.t("quantity")}:</b> {modalContent.length}
              </Typography>
            </Grid>

            <Grid item>
              <Grid sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography>
                  <b>{modalTitle}:</b>
                </Typography>

                <Grid
                  sx={{
                    display: "flex",
                    justifyContent: "space-around",
                    alignItems: "center",
                    gap: "2px",
                    cursor: "pointer",
                    transition: "background-color 0.3s ease, transform 0.2s ease",
                    "&:hover": {
                      backgroundColor: (theme) => theme.palette.action.hover,
                    },
                    "&:active": {
                      backgroundColor: (theme) => theme.palette.action.selected,
                    },
                  }}
                  onClick={handleCopyItems}
                >
                  <ContentCopyOutlinedIcon fontSize={"small"} color={"primary"} />
                  <Typography color={"primary"}>
                    <b>{translate.t(showCopyMessage ? "copied" : "copy")}</b>
                  </Typography>
                </Grid>
              </Grid>

              <Grid sx={{
                "& .MuiInputBase-root": {
                  padding: "5px 0px 5px 5px",
                },
                marginTop: "10px",
              }}>
                <TextField
                  multiline
                  rows={7}
                  value={modalContent.join(", ")}
                  fullWidth
                  disabled={true}
                  sx={{
                    "& .MuiInputBase-input": {
                      "&[disabled]": {
                        color: "#607883",
                        WebkitTextFillColor: "#607883",
                      },
                    },
                    backgroundColor: "#FAFAFA",
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Grid container justifyContent="flex-end" gap={1} padding={2}>
            <Grid item>
              <Button
                color="primary"
                variant="outlined"
                onClick={handleCloseModal}
              >
                {translate.t("close")}
              </Button>
            </Grid>
          </Grid>
        </DialogActions>
      </Dialog>
    </section>
  );
};

export default StockComponent;
